import {
  IconicCards2,
  IconicDeathstar,
  IconicFlag,
  IconicHelpCircle,
  IconicHome,
  IconicInbox,
  // IconicInbox,
  IconicPower,
  IconicShieldTick,
  IconicSlideshow,
} from '@insify/design-system/iconic-icons';
import { useBreakpoint } from '@insify/react-hooks';
import clsx from 'clsx';

import { usePolicies } from '../../api/queries/usePolicies';
import { GTMEvent, useTagManager } from '../../hooks/useTagManager';
import { useAuthStore } from '../../stores/authStore';
import { getAppDomain, useCountrySpecificContent } from '../../utils/localize';

import { BottomNav } from './BottomNav/BottomNav';
import styles from './Menu.module.css';
import { MenuItem, type MenuItemProps } from './MenuItem';
import { NavigationSidebar } from './Sidebar/Sidebar';

import type { InsifyProps } from '@insify/ui';

type Props = InsifyProps;

const getCountrySpecificOptions = (): MenuItemProps[] => {
  const { sendEvent } = useTagManager();

  const { landing_page_contact, landing_page_report_damage } = useCountrySpecificContent();

  return [
    {
      id: 'file-a-claim',
      labelKey: 'claims',
      Icon: IconicFlag,
      href: landing_page_report_damage,
      target: '_blank',
      rel: 'noreferrer',
      onClick: () => sendEvent(GTMEvent.FILE_CLAIM),
    },
    {
      id: 'support',
      labelKey: 'help_center',
      Icon: IconicHelpCircle,
      href: landing_page_contact,
      target: '_blank',
      rel: 'noreferrer',
      className: styles.support,
      onClick: () => sendEvent(GTMEvent.SUPPORT),
    },
  ];
};

const Navigation: React.FC<Props> = ({ className }) => {
  const isAdmin = useAuthStore((state) => state.isAdmin);
  const logout = useAuthStore((state) => state.logout);
  const { sendEvent } = useTagManager();

  const isMobile = !useBreakpoint('(min-width: 960px)');

  const logOut = () => {
    sendEvent(GTMEvent.LOGOUT);
    logout();
  };

  const mainMenuItems: MenuItemProps[] = [
    {
      id: 'home',
      labelKey: 'home',
      Icon: IconicHome,
      to: '/',
      onClick: () => sendEvent(GTMEvent.HOME),
    },
    {
      id: 'products',
      labelKey: 'products',
      Icon: IconicCards2,
      to: '/products',
      onClick: () => sendEvent(GTMEvent.MY_POLICIES),
    },
  ];

  if (getAppDomain() === 'nl' && !isMobile) {
    mainMenuItems.push({
      id: 'my-safety-net',
      labelKey: 'my_safety_net',
      Icon: IconicShieldTick,
      to: '/my-safety-net',
      onClick: () => sendEvent(GTMEvent.MY_SAFETY_NET),
    });
  }

  // TODO: remove this once we also have company policies in GRID
  const { data: policies } = usePolicies();
  const hasPersonalPolicy = policies?.some((policy) => policy.type === 'personal');
  if (hasPersonalPolicy) {
    mainMenuItems.push({
      id: 'invoices',
      labelKey: 'invoices',
      Icon: IconicInbox,
      to: '/invoices',
      onClick: () => sendEvent(GTMEvent.INVOICES),
    });
  }

  const moreMenuItems: MenuItemProps[] = [
    ...getCountrySpecificOptions(),
    {
      id: 'logout',
      labelKey: 'logout',
      Icon: IconicPower,
      onClick: logOut,
    },
  ];

  return isMobile ? (
    <BottomNav menuItems={mainMenuItems} drawerMenuItems={moreMenuItems} />
  ) : (
    <NavigationSidebar className={clsx(styles.navigation)}>
      <nav className={clsx(styles.menu, className)}>
        <ul className={styles.menuItems}>
          {[...mainMenuItems, ...moreMenuItems].map((item) => (
            <MenuItem key={item.id} {...item} />
          ))}
          {isAdmin && <MenuItem to='/impersonate' id={`impersonate`} Icon={IconicDeathstar} labelKey='impersonate' />}
          {isAdmin && (
            <MenuItem to='/slide-designer' id={`slide-designer`} Icon={IconicSlideshow} labelKey='Slide designer' />
          )}
        </ul>
      </nav>
    </NavigationSidebar>
  );
};

export { Navigation as Menu };
